/**
 * The dues engine. SPEC.md §4 — "money must be correct to the rupee and to the day".
 *
 * Two rules matter most, and both have burned an earlier build:
 *   1. A payment CTA appears ONLY when money is actually owed (see `payable`).
 *   2. Early renewers must not lose paid-for days (see `nextPaidTill`).
 *
 * Both the manual admin path and the Razorpay webhook call `nextPaidTill`,
 * so they cannot diverge.
 */

import { PAYABLE_WINDOW, SOON_WINDOW, TIERS } from './constants';
import { memberFee } from './fees';
import { addDays, addMonths, daysTo, formatDate } from './time';

/*
 * A dues subject is any object with { tier, duesPaidTill, linkSent? }:
 *   tier          one of the TIERS keys — annual | student | junior | life
 *   duesPaidTill  ISO date, or null when they have never paid
 *   linkSent      true once a payment link has gone out and we are waiting
 *
 * `duesState` returns { key, label, cls }:
 *   key  life | pending | lapsed | soon | paid
 *   cls  the pill class in the design system:
 *        `.pill.late` / `.soon` / `.ok` / `.pend` / `.sent`
 */

/**
 * Derives dues status from `duesPaidTill` against today.
 * Never stored — it drifts the moment the clock moves. SPEC.md §3.9.
 */
export function duesState(m) {
  if (m.tier === 'life') {
    return { key: 'life', label: 'Life member', cls: 'ok' };
  }
  if (!m.duesPaidTill) {
    return m.linkSent
      ? { key: 'pending', label: 'Link sent · unpaid', cls: 'sent' }
      : { key: 'pending', label: 'Dues pending', cls: 'pend' };
  }
  const days = daysTo(m.duesPaidTill);
  if (days < 0) {
    return { key: 'lapsed', label: `Lapsed ${formatDate(m.duesPaidTill)}`, cls: 'late' };
  }
  if (days <= SOON_WINDOW) {
    return { key: 'soon', label: `${days} days left`, cls: 'soon' };
  }
  return { key: 'paid', label: `Paid till ${formatDate(m.duesPaidTill)}`, cls: 'ok' };
}

/**
 * Whether to show a payment CTA at all.
 *
 * Never true for a life member. Kept as the "is this member relevant to
 * renewal" test; whether the button is PRESSABLE is payableUi() below.
 */
export function payable(m) {
  if (m.tier === 'life') return false;
  const key = duesState(m).key;
  return key === 'pending' || key === 'lapsed' || key === 'soon';
}

/**
 * How the renewal button should render: shown, and whether enabled.
 *
 * Owner's direction, 16 Aug 2026. The button is now VISIBLE to any non-life
 * member and only becomes pressable inside PAYABLE_WINDOW (7 days) of expiry,
 * or once dues are pending or lapsed.
 *
 * The visible-but-disabled shape is the point. The previous behaviour hid the
 * button entirely while a membership was comfortably paid, and the comment
 * that used to live here recorded the opposite complaint — gating on a short
 * window left members staring at "23 days left" with nothing to press. Showing
 * a disabled button with the date it unlocks answers both: renewal is
 * obviously there, obviously not yet, and obviously when.
 *
 * Returns { show, enabled, hint } — hint explains a disabled button, and is
 * null when there is nothing to explain.
 */
export function payableUi(m) {
  if (m.tier === 'life') {
    return { show: false, enabled: false, hint: null };
  }

  const state = duesState(m);

  if (state.key === 'pending' || state.key === 'lapsed') {
    return { show: true, enabled: true, hint: null };
  }

  const days = daysTo(m.duesPaidTill);
  if (days <= PAYABLE_WINDOW) {
    return { show: true, enabled: true, hint: null };
  }

  return {
    show: true,
    enabled: false,
    hint: `Renew from ${formatDate(addDays(m.duesPaidTill, -PAYABLE_WINDOW))}`,
  };
}

/**
 * The new `duesPaidTill` after a payment.
 *
 * Extends from the existing expiry when that is still in the future, so
 * someone renewing three months early keeps those three months. Returns null
 * for a life member — they never expire.
 */
export function nextPaidTill(current, paidOn, tier) {
  const months = TIERS[tier].months;
  if (months === null) return null;
  const base = current && current > paidOn ? current : paidOn;
  return addMonths(base, months);
}

/**
 * Total owed across a roster — everyone lapsed or never-paid, each at the
 * amount THEY owe under the club's fees: joining for the never-paid, renewal
 * for the lapsed. `fees` is the club's raw override map (null = defaults).
 */
export function outstandingTotal(members, fees = null) {
  return members
    .filter((m) => {
      const key = duesState(m).key;
      return key === 'lapsed' || key === 'pending';
    })
    .reduce((sum, m) => sum + memberFee(fees, m), 0);
}

/** Sort order for the admin Dues table: chase the worst first. */
const DUES_ORDER = {
  pending: 0,
  lapsed: 1,
  soon: 2,
  paid: 3,
  life: 4,
};

export function duesSortRank(m) {
  return DUES_ORDER[duesState(m).key];
}

/** Formats rupees the Indian way: ₹1,50,000. */
export function formatMoney(amount) {
  return `₹${amount.toLocaleString('en-IN')}`;
}
